const numberValue = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatNumber = (value, maximumFractionDigits = 0) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits }).format(numberValue(value));

const formatMoney = (value) => {
  const amount = numberValue(value);
  if (Math.abs(amount) >= 1000000000) return `${formatNumber(amount / 1000000000, 2)} tỷ`;
  if (Math.abs(amount) >= 1000000) return `${formatNumber(amount / 1000000, 1)} triệu`;
  return `${formatNumber(amount)} đ`;
};

const formatPercent = (value) => `${formatNumber(value, 1)}%`;

const getDelta = (value) => {
  if (value === null || value === undefined || value === '') {
    return { delta: 'Chưa có kỳ trước', tone: 'neutral' };
  }
  const number = numberValue(value);
  return {
    delta: `${number > 0 ? '+' : ''}${formatNumber(number, 1)}%`,
    tone: number >= 0 ? 'positive' : 'danger',
  };
};

const formatDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit' }).format(date);
};

const formatPeriod = (summary = {}) => {
  if (!summary.datefrom || !summary.dateto) return '30 ngày gần nhất';
  return `${formatDateLabel(summary.datefrom)} - ${formatDateLabel(summary.dateto)}`;
};

const COLORS = ['#ED3237', '#344054', '#F79009', '#12B76A', '#2E90FA', '#7A5AF8', '#667085', '#D92D20'];

const buildSalesOverviewReport = (report, data) => {
  const summary = data?.summary || {};
  const trend = data?.trend || [];
  const salespersons = data?.salespersons || [];
  const statuses = data?.statuses || [];
  const hasCost = summary.cogsamount !== null && summary.cogsamount !== undefined;
  const hasProfit = summary.grossprofit !== null && summary.grossprofit !== undefined;

  const metrics = [
    { label: 'Doanh thu thuần', value: formatMoney(summary.netrevenue), delta: formatMoney(summary.grossrevenue), tone: 'positive', note: 'Doanh thu thuần chưa VAT, đã trừ giảm trừ trả hàng.' },
    { label: 'Đơn ghi nhận doanh số', value: formatNumber(summary.completedordercount), delta: formatNumber(summary.totalordercount), tone: 'neutral', note: 'đơn có doanh số trong kỳ' },
    { label: 'Giá trị đơn bình quân', value: formatMoney(summary.averageordervalue), delta: formatNumber(summary.customercount), tone: 'neutral', note: 'khách hàng phát sinh' },
    { label: 'Tỷ lệ hủy đơn', value: formatPercent(summary.cancellationrate), delta: formatNumber(summary.cancelledordercount), tone: Number(summary.cancelledordercount) > 0 ? 'warning' : 'positive', note: 'đơn đã hủy' },
    { label: 'Tổng chiết khấu', value: formatMoney(summary.discountamount), delta: formatNumber(summary.unitssold), tone: 'neutral', note: 'sản phẩm đã bán' },
    { label: 'Giá trị trả hàng', value: formatMoney(summary.returnamount), delta: formatNumber(summary.salespersoncount), tone: 'warning', note: 'giảm trừ chưa VAT' },
  ];
  if (hasCost) {
    metrics.push({ label: 'Giá vốn', value: formatMoney(summary.cogsamount), delta: 'Dữ liệu nhạy cảm', tone: 'neutral', note: 'chỉ người có quyền' });
  }
  if (hasProfit) {
    metrics.push({ label: 'Lợi nhuận gộp', value: formatMoney(summary.grossprofit), delta: formatPercent(summary.grossmargin), tone: 'positive', note: 'chỉ người có quyền' });
  }

  return {
    ...report,
    subtitle: data?.meta?.revenue_scope_notice || 'Doanh thu bán lẻ, không gồm bán sỉ và chuyển nội bộ. Doanh thu chưa gán cửa hàng được đối chiếu riêng.',
    filters: [
      { label: 'Kỳ báo cáo', value: formatPeriod(summary) },
      { label: 'Loại ngày', value: 'Ngày xuất kho / tạo đơn dịch vụ' },
      { label: 'Phạm vi', value: 'Cửa hàng đang chọn' },
      { label: 'Điều kiện ghi nhận', value: 'Có phiếu xuất / dịch vụ hợp lệ' },
    ],
    metrics,
    panels: [
      {
        type: 'line',
        width: 'span-8',
        title: 'Xu hướng doanh thu',
        subtitle: `Doanh thu thuần theo ngày, kỳ ${formatPeriod(summary)}.`,
        labels: trend.map((item) => formatDateLabel(item.reportdate)),
        series: [
          {
            label: 'Doanh thu thuần',
            color: '#ED3237',
            values: trend.map((item) => numberValue(item.netrevenue) / 1000000),
            area: true,
          },
        ],
        footer: 'Đơn vị biểu đồ: triệu đồng.',
      },
      {
        type: 'table',
        width: 'span-4',
        title: 'Trạng thái đơn hàng',
        subtitle: 'Các đơn ghi nhận doanh số trong kỳ.',
        columns: ['Trạng thái', 'Số đơn', 'Giá trị'],
        rows: statuses.map((item) => [
          item.statusname || 'Chưa xác định',
          formatNumber(item.ordercount),
          formatMoney(item.ordervalue),
        ]),
      },
      {
        type: 'table',
        width: 'span-12',
        title: 'Doanh thu theo nhân viên bán hàng',
        subtitle: 'Doanh thu được chia đều cho các nhân viên bán hàng đã ghi nhận trên đơn.',
        columns: ['Nhân viên bán hàng', 'Số đơn', 'Khách hàng', 'Doanh thu thuần'],
        rows: salespersons.map((item) => [
          item.fullname || 'Chưa xác định nhân viên bán hàng',
          formatNumber(item.ordercount),
          formatNumber(item.customercount),
          formatMoney(item.netrevenue),
        ]),
      },
    ],
  };
};

const buildRevenueByStoreReport = (report, data) => {
  const summary = data?.summary || {};
  const stores = data?.stores || [];
  const trend = (data?.trend || []).slice(-14);
  const revenueDelta = getDelta(summary.revenuechange);
  const orderDelta = getDelta(summary.orderchange);
  const hasProfit = summary.grossprofit !== null && summary.grossprofit !== undefined;
  const metrics = [
    { label: 'Doanh thu', value: formatMoney(summary.revenue), ...revenueDelta, note: 'Doanh thu bán hàng chưa VAT, trước giảm trừ trả hàng.' },
    { label: 'Đơn ghi nhận doanh số', value: formatNumber(summary.ordercount), ...orderDelta, note: 'phần xuất kho và dịch vụ hợp lệ' },
    { label: 'Giá trị đơn bình quân', value: formatMoney(summary.averageordervalue), delta: formatNumber(summary.customercount), tone: 'neutral', note: 'khách hàng phát sinh' },
  ];
  if (hasProfit) {
    metrics.push({
      label: 'Lợi nhuận gộp',
      value: formatMoney(summary.grossprofit),
      delta: formatPercent(summary.grossmargin),
      tone: 'positive',
      note: 'theo giá vốn đã ghi nhận',
    });
  }

  return {
    ...report,
    subtitle: data?.meta?.revenue_scope_notice || 'Doanh thu bán lẻ, không gồm bán sỉ và chuyển nội bộ. Doanh thu chưa gán cửa hàng được đối chiếu riêng.',
    filters: report.filters.map((filter) =>
      filter.label === 'Kỳ' ? { ...filter, value: formatPeriod(summary) } : filter,
    ),
    metrics,
    panels: [
      {
        type: 'bars',
        width: 'span-6',
        title: 'Doanh thu theo cửa hàng',
        subtitle: `Kỳ ${formatPeriod(summary)}. Cửa hàng đang chọn được đánh dấu đỏ.`,
        bars: stores.slice(0, 8).map((store, index) => ({
          label: store.storename,
          value: numberValue(store.revenue) / 1000000,
          displayValue: formatMoney(store.revenue),
          color: store.isselected ? '#ED3237' : COLORS[(index + 1) % COLORS.length],
        })),
      },
      {
        type: 'line',
        width: 'span-6',
        title: 'Xu hướng doanh thu',
        subtitle: 'Doanh thu theo ngày của cửa hàng đang chọn, đơn vị triệu đồng.',
        labels: trend.map((item) => formatDateLabel(item.reportdate)),
        series: [
          {
            label: 'Doanh thu',
            color: '#ED3237',
            values: trend.map((item) => numberValue(item.revenue) / 1000000),
            area: true,
          },
        ],
        footer: `${formatNumber(summary.unitssold)} sản phẩm đã bán trong ${formatNumber(summary.ordercount)} đơn ghi nhận doanh số.`,
      },
      {
        type: 'table',
        width: 'span-12',
        title: 'Hiệu suất cửa hàng',
        subtitle: 'So sánh doanh thu, số đơn, giá trị đơn và lợi nhuận gộp trong cùng phạm vi.',
        columns: [
          'Cửa hàng',
          'Doanh thu',
          'Số đơn',
          'Giá trị đơn TB',
          'SL sản phẩm',
          ...(hasProfit ? ['Biên gộp'] : []),
        ],
        rows: stores.map((store) => [
          store.storename,
          formatMoney(store.revenue),
          formatNumber(store.ordercount),
          formatMoney(store.averageordervalue),
          formatNumber(store.unitssold),
          ...(hasProfit ? [formatPercent(store.grossmargin)] : []),
        ]),
      },
    ],
  };
};

const buildSalesStaffProductivityReport = (report, data) => {
  const summary = data?.summary || {};
  const staff = data?.staff || [];
  const revenueDelta = getDelta(summary.revenuechange);
  const hasProfit = summary.grossprofit !== null && summary.grossprofit !== undefined;

  return {
    ...report,
    subtitle: data?.meta?.revenue_scope_notice || 'Doanh thu bán lẻ, không gồm bán sỉ và chuyển nội bộ. Doanh thu chưa gán cửa hàng được đối chiếu riêng.',
    filters: report.filters.map((filter) =>
      filter.label === 'Kỳ' ? { ...filter, value: formatPeriod(summary) } : filter,
    ),
    metrics: [
      { label: 'Doanh thu/người', value: formatMoney(summary.revenueperstaff), ...revenueDelta, note: 'Doanh thu bán hàng chưa VAT, trước giảm trừ trả hàng.' },
      { label: 'Tổng doanh thu', value: formatMoney(summary.revenue), ...revenueDelta, note: 'Doanh thu bán hàng chưa VAT, trước giảm trừ trả hàng.' },
      { label: 'Đơn ghi nhận doanh số', value: formatNumber(summary.ordercount), delta: formatNumber(summary.staffcount), tone: 'neutral', note: 'nhân viên có phát sinh bán' },
      { label: 'Tỷ lệ bán kèm', value: formatPercent(summary.attachrate), delta: formatNumber(summary.unitssold), tone: 'positive', note: 'sản phẩm đã bán' },
    ],
    panels: [
      {
        type: 'leaderboard',
        width: 'span-4',
        title: 'Xếp hạng nhân viên',
        subtitle: 'Theo doanh thu được chia đều cho các nhân viên bán hàng trên đơn ghi nhận doanh số.',
        items: staff.slice(0, 8).map((item) => ({
          label: item.fullname
            ? [item.username, item.fullname].filter(Boolean).join(' - ')
            : 'Chưa xác định nhân viên bán hàng',
          value: formatMoney(item.revenue),
          meta: hasProfit
            ? `${formatNumber(item.ordercount)} đơn | Biên ${formatPercent(item.grossmargin)}`
            : `${formatNumber(item.ordercount)} đơn`,
        })),
      },
      {
        type: 'bars',
        width: 'span-4',
        title: 'Tỷ lệ bán kèm',
        subtitle: 'Tỷ lệ đơn có từ hai sản phẩm trở lên.',
        bars: staff.slice(0, 8).map((item, index) => ({
          label: item.fullname || 'Chưa xác định nhân viên bán hàng',
          value: numberValue(item.attachrate),
          displayValue: formatPercent(item.attachrate),
          color: COLORS[index % COLORS.length],
        })),
      },
      {
        type: 'table',
        width: 'span-4',
        title: 'Chi tiết năng suất',
        subtitle: 'Doanh thu theo phần được phân bổ; số đơn tính các đơn nhân viên tham gia bán.',
        columns: ['Nhân viên', 'Doanh thu', 'Đơn', 'Giá trị đơn TB', ...(hasProfit ? ['Biên gộp'] : [])],
        rows: staff.map((item) => [
          item.fullname || 'Chưa xác định nhân viên bán hàng',
          formatMoney(item.revenue),
          formatNumber(item.ordercount),
          formatMoney(item.averageordervalue),
          ...(hasProfit ? [formatPercent(item.grossmargin)] : []),
        ]),
      },
    ],
  };
};

export const buildRetailReportFromApi = (report, data) => {
  if (!data) return report;
  if (report.id === 'overview') return buildSalesOverviewReport(report, data);
  if (report.id === 'revenue-by-store') return buildRevenueByStoreReport(report, data);
  if (report.id === 'sales-staff-productivity') return buildSalesStaffProductivityReport(report, data);
  return report;
};
